/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Custom announcement bar horizontal carousel functionality
document.addEventListener("DOMContentLoaded", function () {
	const announcementBars = document.querySelectorAll(
		"announcement-bar[horizontal-carousel]"
	);

	announcementBars.forEach((bar) => {
		const items = bar.querySelectorAll("announcement-bar-item");
		const list = bar.querySelector(".announcement-bar__list");
		let currentIndex = 0;

		if (items.length > 1 && list) {
			// Set initial state
			items.forEach((item, index) => {
				item.classList.toggle("selected", index === 0);
				item.classList.remove("hidden");
			});

			// Update transform for horizontal sliding
			function updateTransform(index) {
				const translateX = -(index * (100 / items.length));
				list.style.transform = `translateX(${translateX}%)`;
			}

			// Handle navigation
			const prevButton = bar.querySelector('[data-action="prev"]');
			const nextButton = bar.querySelector('[data-action="next"]');

			if (prevButton) {
				prevButton.addEventListener("click", () => {
					currentIndex =
						(currentIndex - 1 + items.length) % items.length;
					items.forEach((item, index) => {
						item.classList.toggle(
							"selected",
							index === currentIndex
						);
					});
					updateTransform(currentIndex);
				});
			}

			if (nextButton) {
				nextButton.addEventListener("click", () => {
					currentIndex = (currentIndex + 1) % items.length;
					items.forEach((item, index) => {
						item.classList.toggle(
							"selected",
							index === currentIndex
						);
					});
					updateTransform(currentIndex);
				});
			}

			// Auto-play functionality
			const cycleSpeed = bar.getAttribute("cycle-speed");
			if (cycleSpeed && bar.hasAttribute("auto-play")) {
				setInterval(() => {
					currentIndex = (currentIndex + 1) % items.length;
					items.forEach((item, index) => {
						item.classList.toggle(
							"selected",
							index === currentIndex
						);
					});
					updateTransform(currentIndex);
				}, parseInt(cycleSpeed) * 1000);
			}
		}
	});
});

// Infinite marquee functionality for mobile
document.addEventListener("DOMContentLoaded", function () {
	// Find announcement bars that have the marquee setting enabled
	const announcementBars = document.querySelectorAll("announcement-bar");

	announcementBars.forEach((bar) => {
		// Only apply marquee behavior on mobile
		function handleMarquee() {
			if (window.innerWidth <= 1023) {
				// Check if this section has marquee enabled by looking for the CSS animation
				const list = bar.querySelector(".announcement-bar__list");
				if (
					list &&
					getComputedStyle(list).animationName === "marquee-scroll"
				) {
					// Ensure all items are visible for marquee on mobile
					const items = bar.querySelectorAll("announcement-bar-item");
					items.forEach((item) => {
						item.classList.remove("hidden");
					});

					// Pause marquee on hover (optional)
					bar.addEventListener("mouseenter", function () {
						if (list) {
							list.style.animationPlayState = "paused";
						}
					});

					bar.addEventListener("mouseleave", function () {
						if (list) {
							list.style.animationPlayState = "running";
						}
					});
				}
			}
		}

		// Initial call
		handleMarquee();

		// Handle resize events
		window.addEventListener("resize", handleMarquee);
	});
});

// Infinite marquee functionality for announcement-bar-footer on mobile
document.addEventListener("DOMContentLoaded", function () {
	// Find announcement bar footers that have the marquee setting enabled
	const announcementBarFooters = document.querySelectorAll(
		"announcement-bar-footer"
	);

	announcementBarFooters.forEach((bar) => {
		// Only apply marquee behavior on mobile
		function handleMarquee() {
			if (window.innerWidth <= 1023) {
				// Check if this section has marquee enabled by looking for the CSS animation
				const list = bar.querySelector(
					".announcement-bar-footer__list"
				);
				if (
					list &&
					getComputedStyle(list).animationName === "marquee-scroll"
				) {
					// Ensure all items are visible for marquee on mobile
					const items = bar.querySelectorAll(
						"announcement-bar-footer-item"
					);
					items.forEach((item) => {
						item.classList.remove("hidden");
					});

					// Pause marquee on hover (optional)
					bar.addEventListener("mouseenter", function () {
						if (list) {
							list.style.animationPlayState = "paused";
						}
					});

					bar.addEventListener("mouseleave", function () {
						if (list) {
							list.style.animationPlayState = "running";
						}
					});
				}
			}
		}

		// Initial call
		handleMarquee();

		// Handle resize events
		window.addEventListener("resize", handleMarquee);
	});
});

// Testimonials carousel arrow navigation
document.addEventListener("DOMContentLoaded", function () {
	const testimonialsCarousels = document.querySelectorAll(
		"testimonials-carousel"
	);

	testimonialsCarousels.forEach((carousel) => {
		const list = carousel.querySelector(".testimonials-carousel__list");
		const prevButton = carousel.querySelector('[data-action="prev"]');
		const nextButton = carousel.querySelector('[data-action="next"]');

		if (list && prevButton && nextButton) {
			const items = carousel.querySelectorAll(
				".testimonials-carousel__item"
			);
			let currentIndex = 0;

			console.log("Testimonials carousel found:", items.length, "items");

			// Calculate scroll amount based on item width and gap
			function getScrollAmount() {
				const itemWidth = items[0].offsetWidth;
				const gap = 16; // 4 * 4px (gap-4 in Tailwind)
				return itemWidth + gap;
			}

			// Get the maximum scroll position
			function getMaxScroll() {
				return list.scrollWidth - list.clientWidth;
			}

			// Check if we're at the boundary with tolerance
			function isAtBoundary(direction) {
				const tolerance = 5; // 5px tolerance
				if (direction === "next") {
					return list.scrollLeft >= getMaxScroll() - tolerance;
				} else if (direction === "prev") {
					return list.scrollLeft <= tolerance;
				}
				return false;
			}

			// Scroll to specific index
			function scrollToIndex(index) {
				// Don't scroll if we're already at the boundary
				if (index < 0 || index >= items.length) {
					return;
				}

				currentIndex = index;
				const scrollAmount = getScrollAmount() * index;
				console.log(
					"Scrolling to index:",
					index,
					"amount:",
					scrollAmount
				);
				list.scrollTo({
					left: scrollAmount,
					behavior: "smooth",
				});
			}

			// Previous button click
			prevButton.addEventListener("click", (e) => {
				e.preventDefault();
				if (!isAtBoundary("prev")) {
					console.log("Previous button clicked");
					scrollToIndex(currentIndex - 1);
				} else {
					console.log("At left boundary, preventing scroll");
				}
			});

			// Next button click
			nextButton.addEventListener("click", (e) => {
				e.preventDefault();
				if (!isAtBoundary("next")) {
					console.log("Next button clicked");
					scrollToIndex(currentIndex + 1);
				} else {
					console.log("At right boundary, preventing scroll");
				}
			});

			// Update current index on scroll
			list.addEventListener("scroll", () => {
				const scrollAmount = getScrollAmount();
				const newIndex = Math.round(list.scrollLeft / scrollAmount);
				if (
					newIndex !== currentIndex &&
					newIndex >= 0 &&
					newIndex < items.length
				) {
					currentIndex = newIndex;
				}
			});
		}
	});
});

// --- Product image zoom z-index management ---
function handleProductZoomZIndex() {
	var zoom = document.querySelector(
		".product-image-zoom, product-image-zoom"
	);
	var media = document.querySelector(".product__media");
	if (!zoom || !media) return;

	function updateZIndex() {
		if (zoom.classList.contains("pswp--open")) {
			media.style.zIndex = "10";
		} else {
			media.style.zIndex = "";
		}
	}

	// Observe class changes
	var observer = new MutationObserver(updateZIndex);
	observer.observe(zoom, { attributes: true, attributeFilter: ["class"] });

	// Initial check
	updateZIndex();
}
document.addEventListener("DOMContentLoaded", handleProductZoomZIndex);

/* ── Cart insurance calculator (Deep Rest Bundle) ── */
(function () {
	var STORAGE_KEY = "zyvo_insurance_selection";

	function money(cents) {
		var value = (Number(cents) || 0) / 100;
		return (
			value.toLocaleString("de-DE", {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}) + "\u00A0€"
		);
	}

	function calcRefund(priceCents, value, maxFee) {
		var price = (Number(priceCents) || 0) / 100;
		var pct = Number(value) || 0;
		var cap = Number(maxFee) || 0;
		var refund = Math.min(Math.round((price * pct) / 100), cap);
		return Math.round(refund * 100);
	}

	function readJson(el) {
		if (!el) return null;
		try {
			return JSON.parse(el.textContent);
		} catch (e) {
			return null;
		}
	}

	function persistSelection(payload) {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
		} catch (e) {}
	}

	function fillSelect(root) {
		var select = root.querySelector("[data-cart-insurance-select]");
		var insurers = readJson(root.querySelector("[data-cart-insurance-insurers]")) || [];
		var selectedName = root.getAttribute("data-selected-name") || "";
		var selectedType = root.getAttribute("data-selected-type") || "";

		select.innerHTML = "";

		insurers
			.slice()
			.sort(function (a, b) {
				return String(a.name).localeCompare(String(b.name), "de");
			})
			.forEach(function (ins) {
				var opt = document.createElement("option");
				opt.value = ins.name;
				opt.textContent = ins.name;
				opt.dataset.value = ins.value;
				opt.dataset.maxFee = ins.max_fee;
				opt.dataset.type = "gesetzlich";
				if (ins.name === selectedName) opt.selected = true;
				select.appendChild(opt);
			});

		if (selectedType === "privat" || (selectedName && !insurers.some(function (i) { return i.name === selectedName; }))) {
			var custom = document.createElement("option");
			custom.value = selectedName || "Privat";
			custom.textContent = selectedName || "Privat versichert";
			custom.dataset.value = root.getAttribute("data-selected-value") || "0";
			custom.dataset.maxFee = root.getAttribute("data-selected-max-fee") || "0";
			custom.dataset.type = selectedType || "privat";
			custom.selected = true;
			select.appendChild(custom);
		}

		if (!select.value && select.options.length) {
			select.options[0].selected = true;
		}
	}

	function renderAmounts(root) {
		var select = root.querySelector("[data-cart-insurance-select]");
		var option = select.options[select.selectedIndex];
		var priceCents = Number(root.getAttribute("data-price-cents")) || 0;
		var compareCents = Number(root.getAttribute("data-compare-cents")) || 0;
		var value = option ? Number(option.dataset.value) || 0 : 0;
		var maxFee = option ? Number(option.dataset.maxFee) || 0 : 0;
		var type = option ? option.dataset.type : "gesetzlich";
		var bonusEnabled = root.getAttribute("data-completion-bonus") === "1";
		var bonusLabel = root.getAttribute("data-bonus-label") || "ZYVO Kursabschluss-Bonus";
		var bonusFinalLabel = root.getAttribute("data-bonus-final-label") || "Teilnahme effektiv kostenlos";

		var uvpEl = root.querySelector("[data-cart-insurance-uvp]");
		var payableEl = root.querySelector("[data-cart-insurance-payable]");
		var refundEl = root.querySelector("[data-cart-insurance-refund]");
		var bonusRow = root.querySelector("[data-cart-insurance-bonus-row]");
		var bonusEl = root.querySelector("[data-cart-insurance-bonus]");
		var bonusLabelEl = root.querySelector("[data-cart-insurance-bonus-label]");
		var finalLabelEl = root.querySelector("[data-cart-insurance-final-label]");
		var effectiveEl = root.querySelector("[data-cart-insurance-effective]");

		if (uvpEl) uvpEl.textContent = money(compareCents);
		if (payableEl) payableEl.textContent = money(priceCents);
		if (bonusLabelEl) bonusLabelEl.textContent = bonusLabel;

		var refundCents = 0;
		if (type === "privat") {
			if (refundEl) refundEl.textContent = "variiert";
			if (bonusRow) bonusRow.hidden = !bonusEnabled;
			if (bonusEnabled) {
				if (bonusEl) bonusEl.textContent = "Restbetrag";
				if (finalLabelEl) finalLabelEl.textContent = bonusFinalLabel;
				if (effectiveEl) effectiveEl.textContent = "0\u00A0€";
			} else {
				if (finalLabelEl) finalLabelEl.textContent = "Effektivpreis";
				if (effectiveEl) effectiveEl.textContent = "ab 0\u00A0€";
			}
			return { name: option ? option.value : "", value: value, max_fee: maxFee, type: type, refundCents: 0 };
		}

		refundCents = calcRefund(priceCents, value, maxFee);
		var gapCents = Math.max(priceCents - refundCents, 0);
		if (refundEl) refundEl.textContent = "– " + money(refundCents);

		if (bonusEnabled) {
			if (bonusRow) bonusRow.hidden = false;
			if (bonusEl) bonusEl.textContent = gapCents > 0 ? "– " + money(gapCents) : "0\u00A0€";
			if (finalLabelEl) finalLabelEl.textContent = bonusFinalLabel;
			if (effectiveEl) effectiveEl.textContent = "0\u00A0€";
		} else {
			if (bonusRow) bonusRow.hidden = true;
			if (finalLabelEl) finalLabelEl.textContent = "Effektivpreis";
			if (effectiveEl) effectiveEl.textContent = money(gapCents);
		}

		return {
			name: option ? option.value : "",
			value: value,
			max_fee: maxFee,
			type: type,
			refundCents: refundCents,
		};
	}

	function mergeProperties(root, selection) {
		var existing = readJson(root.querySelector("[data-cart-insurance-props]")) || {};
		existing._insurance_name = selection.name || "";
		existing._insurance_type = selection.type || "";
		existing._insurance_value = selection.value != null ? String(selection.value) : "";
		existing._insurance_max_fee = selection.max_fee != null ? String(selection.max_fee) : "";
		return existing;
	}

	function updateLineItem(root, selection) {
		var key = root.getAttribute("data-line-key");
		var quantity = Number(root.getAttribute("data-quantity")) || 1;
		if (!key) return Promise.resolve();

		root.classList.add("is-updating");
		var properties = mergeProperties(root, selection);
		var cartChangeUrl =
			(window.themeVariables &&
				window.themeVariables.routes &&
				window.themeVariables.routes.cartChangeUrl) ||
			"/cart/change";

		return fetch(cartChangeUrl + ".js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({
				id: key,
				quantity: quantity,
				properties: properties,
				sections: ["mini-cart"],
			}),
		})
			.then(function (res) {
				if (!res.ok) return res.json().then(function (err) { throw err; });
				return res.json();
			})
			.then(function (cartContent) {
				persistSelection({
					type: selection.type,
					label: selection.name,
					name: selection.name,
					value: selection.value,
					max_fee: selection.max_fee,
					updated_at: Date.now(),
				});
				document.documentElement.dispatchEvent(
					new CustomEvent("cart:updated", {
						bubbles: true,
						detail: { cart: cartContent },
					})
				);
				document.documentElement.dispatchEvent(
					new CustomEvent("cart:refresh", {
						bubbles: true,
						detail: { cart: cartContent, openMiniCart: false },
					})
				);
			})
			.catch(function (err) {
				console.error("Insurance line update failed", err);
				root.classList.remove("is-updating");
			});
	}

	function initRoot(root) {
		if (root.dataset.insuranceReady === "1") return;
		root.dataset.insuranceReady = "1";
		fillSelect(root);
		renderAmounts(root);

		var select = root.querySelector("[data-cart-insurance-select]");
		select.addEventListener("change", function () {
			var selection = renderAmounts(root);
			updateLineItem(root, selection);
		});
	}

	function initAll(scope) {
		(scope || document)
			.querySelectorAll("[data-cart-insurance]")
			.forEach(initRoot);
	}

	document.addEventListener("DOMContentLoaded", function () {
		initAll(document);

		var drawer = document.querySelector("cart-drawer");
		if (drawer && window.MutationObserver) {
			var timer = null;
			new MutationObserver(function () {
				clearTimeout(timer);
				timer = setTimeout(function () {
					initAll(drawer);
				}, 50);
			}).observe(drawer, { childList: true, subtree: true });
		}
	});

	function getInfoModal() {
		return {
			overlay: document.querySelector("[data-cart-insurance-info-overlay]"),
			modal: document.querySelector("[data-cart-insurance-info-modal]"),
		};
	}

	function openInfoModal() {
		var parts = getInfoModal();
		if (!parts.modal) return;
		parts.overlay.hidden = false;
		parts.modal.hidden = false;
		parts.modal.setAttribute("aria-hidden", "false");
	}

	function closeInfoModal() {
		var parts = getInfoModal();
		if (!parts.modal) return;
		parts.overlay.hidden = true;
		parts.modal.hidden = true;
		parts.modal.setAttribute("aria-hidden", "true");
	}

	document.addEventListener("click", function (event) {
		if (event.target.closest("[data-cart-insurance-info]")) {
			event.preventDefault();
			openInfoModal();
			return;
		}
		if (
			event.target.closest("[data-cart-insurance-info-close]") ||
			event.target.matches("[data-cart-insurance-info-overlay]")
		) {
			closeInfoModal();
		}
	});

	document.addEventListener("keydown", function (event) {
		if (event.key === "Escape") closeInfoModal();
	});
})();

